import {
  createContext, useContext, useEffect, useMemo
} from 'react';

import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { ZBError } from '@zenbusiness/application-commons-logging';

import { useDashboardPath } from '#client/hooks/useDashboardPath';
import { useOrderTrackerStatus } from '#client/hooks/useOrderTrackerStatus';
import { PRODUCTS_KEY } from '#client/pages/Products/constants';

import { useProductDetails } from './useProductDetails';
import { useFormationStatus } from '../hooks/useFormationStatus';

const ProductLandingContext = createContext();

const useProductLandingContext = () => {
  const context = useContext(ProductLandingContext);

  if (!context) {
    throw new ZBError('useProductLanding must be used within a ProductLandingContext.Provider');
  }

  return context;
};

const ProductLandingProvider = ({ children, productId }) => {
  const navigate = useNavigate();
  const productsPath = useDashboardPath(`/${PRODUCTS_KEY}`);
  const productConfig = useProductDetails(productId);
  const formationStatus = useFormationStatus();
  const { entitlements } = useBusinessEntity();
  const { orderTasksWithStatus } = useOrderTrackerStatus();

  // Check if product has a feature flag gate configuration
  const { flagKey, redirectPath } = productConfig?.productGates?.flagGate || {};
  // Only evaluate flags if product has a flagGate config, otherwise skip evaluation.
  const isFlagEnabled = useExperimentFlag(
    flagKey || '',
    false,
    false,
    !flagKey
  );

  const fallbackRedirectPath = useDashboardPath(redirectPath || `/${PRODUCTS_KEY}`);

  useEffect(function redirects() {
    // Redirect if product has a flagGate config and the flag is disabled
    if (flagKey && !isFlagEnabled) {
      navigate(fallbackRedirectPath, { replace: true });
      return;
    }

    // Redirect to products list page when product is not found
    if (!productConfig) {
      navigate(productsPath, { replace: true });
    }
  }, [navigate, productsPath, productConfig, flagKey, isFlagEnabled, fallbackRedirectPath]);

  // Extract hero configuration with defaults
  const heroConfig = useMemo(() => {
    const hero = productConfig?.hero;
    if (!hero) return {};

    return {
      packages: hero.buyBox || [],
      comparePackages: hero.comparePackages,
      plans: hero.plans,
      getBuyButtonConfig: productConfig.getBuyButtonConfig || (() => ({
        isBuyEnabled: false,
        ctaText: 'productPage.common.buyBox.buttonDisabled.label',
        ctaDisabledTooltip: 'productPage.common.buyBox.buttonDisabled.tooltip'
      }))
    };
  }, [productConfig]);

  const contextValue = useMemo(() => ({
    productConfig,
    productId,
    formationStatus,
    entitlements,
    orderTasksWithStatus,
    ...heroConfig
  }), [productConfig, productId, formationStatus, entitlements, heroConfig, orderTasksWithStatus]);

  // Render content based on product component gate configuration
  const content = (() => {
    const gateComponents = productConfig?.productGates?.componentGates || [];

    if (!Array.isArray(gateComponents) || !gateComponents.length) {
      return children;
    }

    // Nest component gates from last to first, wrapping each one around the previous one, with children at the innermost position (most nested component)
    return gateComponents.reduceRight(
      (acc, { Component, props }) => (
        <Component {...props}>
          {acc}
        </Component>
      ),
      children
    );
  })();

  return (
    <ProductLandingContext.Provider value={contextValue}>
      {content}
    </ProductLandingContext.Provider>
  );
};

ProductLandingProvider.propTypes = {
  children: PropTypes.node.isRequired,
  productId: PropTypes.string.isRequired
};

export { ProductLandingProvider, useProductLandingContext };
